import { Hint, Modal, PrimaryButton, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import FormInputRhf from 'shared/components/form-input-rhf';
import FormSelectRhf from 'shared/components/form-select-rhf';
import FormUrlPathInputRhf from 'shared/components/form-url-path-input-rhf';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUserDomains from 'shared/hooks/use-user-domains';
import { createCourse } from '../courses/api/courses-api';
import { CourseCreateInterface, CourseInterface, CourseResponseErrorsInterface } from '../courses/types/course-interface';
import CourseThemeSelect from './theme-select';
import { AddIcon } from '@systemeio/ui/icons';
type CourseFormValues = Pick<CourseCreateInterface, 'domain' | 'name' | 'path' | 'theme'>;
interface CourseCreateModalProps {
  onSave: (course: CourseInterface) => void;
}
const CLASSIC_THEME_ID = 1;
const defaultValues: CourseFormValues = {
  domain: undefined,
  name: '',
  path: '',
  theme: CLASSIC_THEME_ID
};
const CourseCreateModal = ({
  onSave
}: CourseCreateModalProps) => {
  const {
    t
  } = useLocoTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const {
    userDomains,
    userDefaultDomain,
    userDomainsSelect
  } = useUserDomains();
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: {
      errors
    },
    setError,
    clearErrors
  } = useForm<CourseFormValues>({
    defaultValues
  });
  useEffect(() => {
    if (userDefaultDomain) {
      setValue('domain', userDefaultDomain.id);
    }
  }, [userDefaultDomain]);
  const handleClose = () => {
    reset();
    setIsOpen(false);
  };
  const onSubmit: SubmitHandler<CourseFormValues> = async (values: CourseFormValues) => {
    try {
      clearErrors();
      setIsFetching(true);
      const createdCourse = await createCourse(values);
      setIsFetching(false);
      toast.success(t('global.changes_saved'));
      handleClose();
      onSave(createdCourse);
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        if (e.errors.fields) {
          const courseEditErrors = e.errors as unknown as CourseResponseErrorsInterface;
          let common = '';
          Object.entries(courseEditErrors.fields).forEach(([key, error]) => {
            setError(key as 'domain' | 'name' | 'path' | 'theme', {
              message: error?.join('')
            }), common += error?.join('');
          });
          setError('root', {
            message: common
          });
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setError('root', {
            message: commonErrors?.join('')
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  return <>
      <PrimaryButton onClick={() => setIsOpen(true)} className="flex" testAttributePostfix="add-course-button">
        <AddIcon />
        <span className="hidden sm:flex">{t('dashboard.course.add_new_course')}</span>
      </PrimaryButton>
      <Modal opened={isOpen} onClose={handleClose} title={t('dashboard.course.add_new_course')} className="md:max-w-5/6 w-full md:w-2/3">
        {userDomains && <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
            <div className="flex flex-col gap-5 md:flex-row">
              <FormInputRhf control={control} name="name" rules={{
            required: t('dashboard.validation.empty_error.title')
          }} setValue={setValue} label={t('global.name')} error={errors.name?.message} className={'flex-auto'} required data-test-id="add-course-name-input" />
              <FormSelectRhf control={control} name="domain" data={userDomainsSelect} label={t('global.domain')} className={'sm:min-w-0'} setValue={setValue} rules={{
            required: t('dashboard.validation.empty_error.title')
          }} required error={errors.domain?.message} withoutCloseIcon dataTest={{
            ['data-test-id']: 'add-course-domain-input'
          }} />
            </div>
            <FormUrlPathInputRhf control={control} name="path" setValue={setValue} rules={{
          required: t('dashboard.validation.empty_error.title')
        }} label={<Hint label={t('dashboard.funnel.configuration.webinar.url_path_popover-2')}>
                  {t('global.url_path')}
                </Hint>} additionalPath="school/course" required domainName={userDomains.find(el => el.id === watch('domain'))?.name} error={errors.path?.message} data-test-id="add-course-url-path-input" />
            <CourseThemeSelect control={control} setValue={setValue} />
            {errors.root?.message && errors.root.message?.length !== 0 && <div className={'flex justify-center'}>
                <p className="text-red px-6 text-sm" data-test-id="add-course-common-error">
                  {errors.root.message}
                </p>
              </div>}
            <PrimaryButton isFetching={isFetching} type="submit" className="self-center" data-test-id="add-course-save-button">
              {t('global.save')}
            </PrimaryButton>
          </form>}
      </Modal>
    </>;
};
export default CourseCreateModal;